import React from 'react';
import { noop } from 'lodash';
import { Modal } from 'syngenta-digital-cropwise-react-ui-kit';
import UiKitButton from 'components/UiKit/Button';
import './index.css';

type UiKitConfirmModalProps = {
  open: boolean;
  // Called for every way of dismissing: the close button, the mask, Escape
  // and Cancel.
  close: () => void;
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  isSaving?: boolean;
  hasError?: boolean;
  // grow-ui's "error" type: a destructive action, confirmed with a red button.
  danger?: boolean;
};

/**
 * A dialog with Cancel and Confirm on the right, taking the props of the
 * grow-ui SimpleModal ("basic" type) it replaces.
 */
const UiKitConfirmModal = ({
  open,
  close,
  children,
  title,
  icon,
  onConfirm = noop,
  onCancel = noop,
  confirmLabel = 'Ok',
  cancelLabel = 'Cancel',
  isSaving = false,
  hasError = false,
  danger = false
}: UiKitConfirmModalProps) => (
  <Modal
    open={open}
    onCancel={close}
    closable={Boolean(title)}
    width={512}
    className={`cwf-kit-confirm-modal${
      title ? '' : ' cwf-kit-confirm-modal--untitled'
    }`}
    // grow-ui's SimpleModal sat in the middle of the screen; the kit's
    // default is a fixed offset from the top.
    centered
    destroyOnClose
    // Fade in place, as grow-ui's SimpleModal did, rather than zoom out of
    // the button that opened it.
    transitionName="syt-antd-fade"
    title={
      title ? (
        <span className="flex items-center gap-2">
          {icon}
          {title}
        </span>
      ) : (
        undefined
      )
    }
    footer={
      <div className="flex justify-end gap-2">
        <UiKitButton
          variant="outline"
          onClick={() => {
            onCancel();
            close();
          }}
        >
          {cancelLabel}
        </UiKitButton>
        <UiKitButton
          variant="primary"
          danger={danger}
          onClick={onConfirm}
          disabled={isSaving || hasError}
          loading={isSaving}
        >
          {confirmLabel}
        </UiKitButton>
      </div>
    }
  >
    <div className="text-sm text-neutral-1000">{children}</div>
  </Modal>
);

export default UiKitConfirmModal;
